<template>
  <el-drawer
    :direction="direction"
    :with-header="false"
    :modal-append-to-body="false"
    class="sidebar__sub-merchants-drawer__wrapper"
    custom-class="sidebar__sub-merchants-drawer"
    :visible="visible"
    v-on="$listeners"
  >
    <div>
      <div class="sidebar__sub-merchants">
        <div class="sidebar__sub-merchants-head">
          <div class="sidebar__sub-merchants-head-search">
            <el-input
              :value="subMerchantsSearchText"
              clearable
              class="search-input"
              :placeholder="$t('sidebar_search_placeholder')"
              @input="handleInputMerchantSearchBox"
            >
              <span slot="prefix" class="sidebar__search-svg-container">
                <svg-icon icon-class="search" />
              </span>
            </el-input>
          </div>
          <div class="sidebar__sub-merchants-head-text">
            {{ subMerchantAccounts.length }}
            {{ $t('sidebar_accounts_found') }}
          </div>
        </div>
        <div
          ref="merchantAccountList"
          class="sidebar__sub-merchants-content"
          :class="{ 'is-scrollable': isScrollable }"
        >
          <div v-if="searchNotFound" class="sidebar__sub-merchants-no-results">
            {{ $t('sidebar_no_results') }}
          </div>
          <div
            v-for="item in subMerchantAccounts"
            :key="item.merchantId"
            class="sidebar__sub-merchants-item"
            :class="{
              'is-active': item.merchantId === selectedMerchant,
            }"
            @click="handleSelectSubMerchant(item.merchantId)"
          >
            <div class="sidebar__sub-merchants-item__svg-container">
              <svg-icon icon-class="store-solid" size="20px" />
            </div>
            <div class="sidebar__sub-merchants-item__name-container">
              <el-tooltip
                placement="right"
                :open-delay="200"
                :content="getLocalizedSubMerchantName(item)"
                :z-index="2201"
              >
                <div class="sidebar__sub-merchants-item-name ellipsis">
                  {{ getLocalizedSubMerchantName(item) }}
                </div>
              </el-tooltip>
            </div>
            <div v-if="item.merchantId === selectedMerchant">
              <svg-icon icon-class="check-filled" size="20px" color="#9600F1" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import { getLocalizedValue } from '@/utils/i18n'
import TrackingEvent from '@/plugins/tracking/event'

export default {
  name: 'SidebarMerchantAccountList',
  props: {
    direction: {
      type: String,
      required: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    selectedMerchant: {
      type: String,
      required: true,
    },
    subMerchants: {
      type: Array,
      required: true,
    },
    subMerchantAccounts: {
      type: Array,
      required: true,
    },
    subMerchantsSearchText: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isScrollable: false,
    }
  },
  computed: {
    searchNotFound() {
      return (
        this.subMerchantsSearchText && this.subMerchantAccounts.length === 0
      )
    },
  },
  mounted() {
    this.calculateContentHeight()
  },
  updated() {
    this.calculateContentHeight()
  },
  methods: {
    calculateContentHeight() {
      if (!this.$refs.merchantAccountList) {
        return
      }

      const { scrollHeight, clientHeight } = this.$refs.merchantAccountList

      this.isScrollable = scrollHeight > clientHeight
    },
    handleSelectSubMerchant(merchantId) {
      this.$track.event(TrackingEvent.global_navbar_accounts_list_clicked)
      this.$emit('select', merchantId)
    },
    getLocalizedSubMerchantName(item) {
      return getLocalizedValue(item.merchantName, item.arMerchantName)
    },
    handleInputMerchantSearchBox(value) {
      this.$track.event(TrackingEvent.global_navbar_accounts_search_interacted)
      this.$emit('update:subMerchantsSearchText', value)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/lavender/design.scss';

.sidebar__sub-merchants-drawer__wrapper {
  --background-color: var(--tamara-color-background-neutral-low);
  --scollbar-color: var(--tamara-color-brand-700);
  --sub-merchants-item-height: 24px;
  --sub-merchants-item-space-between: 16px;
  --sub-merchants-item-name-container-with: 162px;
  --sub-merchants-item-svg-store-color: var(--tamara-color-content-mid);

  background-color: rgb(0 0 0 / 50%);

  [dir='ltr'] & {
    left: var(--sidebar-width);
  }

  [dir='rtl'] & {
    right: var(--sidebar-width);
  }

  ::v-deep {
    .sidebar__sub-merchants-drawer {
      top: var(--header-height);
      width: var(--sidebar-drawer-width) !important;
      background-color: var(--background-color);

      [dir='ltr'] & {
        padding: 0;
        border-left: 1px solid var(--tamara-color-border-mid);
        border-top-right-radius: 20px;
      }

      [dir='rtl'] & {
        padding: 4px 20px 20px 0;
        border-right: 1px solid var(--tamara-color-border-mid);
        border-top-left-radius: 12px;
        border-top-right-radius: 0;
      }

      .sidebar__sub-merchants-head {
        padding: 20px 20px 20px 21px;

        &-search {
          position: relative;

          .search-input {
            .el-input__inner {
              height: 44px;
              padding: 10px 14px 10px 44px;
              font-family: var(--tamara-font-family-secondary);
              font-size: 11px;
              font-weight: 400;
              line-height: 13.2px;
              text-align: start;
              border-radius: 62px;

              &:hover,
              &:focus,
              &:active {
                border-color: #999;
              }

              [dir='rtl'] & {
                padding: 10px 44px 10px 14px;
              }
            }

            .el-input__prefix {
              left: 14px;
              display: flex;
              align-items: center;

              [dir='rtl'] & {
                right: 14px;
                left: unset;
              }
            }

            .el-input__suffix {
              right: 14px;
              font-size: 16px;

              [dir='rtl'] & {
                right: unset;
                left: 14px;
              }
            }
          }

          .sidebar__search-svg-container {
            font-size: 24px;
            color: #999;
          }
        }

        &-text {
          @extend .tamara-english-regular-3;

          margin-top: 8px;
          color: var(--tamara-color-content-low);
          text-align: start;
        }
      }

      .sidebar__sub-merchants-content {
        height: calc(100vh - var(--header-height) - 108px);
        padding: 4px 0 20px 0;
        margin-top: 0;
        overflow-y: auto;
        scrollbar-color: var(--scollbar-color) var(--background-color);

        &.is-scrollable {
          padding-inline-end: 8px;

          .sidebar__sub-merchants-item {
            padding-inline-end: 12px;

            &.is-active {
              --sub-merchants-item-name-container-with: 149px;
            }
          }
        }

        .sidebar__sub-merchants-no-results {
          color: #fff;
        }
      }

      .sidebar__sub-merchants-item {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        height: var(--sub-merchants-item-height);
        height: 56px;
        padding-inline-start: 20px;
        padding-inline-end: 20px;
        padding-top: var(--sub-merchants-item-space-between);
        padding-bottom: var(--sub-merchants-item-space-between);
        color: var(--tamara-color-content-high);
        text-align: start;
        cursor: pointer;
        border-bottom: 1px solid var(--tamara-color-border-low);
        transition: color 0.3s ease-in-out;

        @extend .tamara-english-regular-2;

        > * {
          flex: 0 0 24px;
        }

        &.is-active {
          --sub-merchants-item-name-container-with: 149px;
          --sub-merchants-item-svg-store-color: var(--tamara-color-brand-700);

          color: var(--tamara-color-brand-700);
          transition: color 0.3s ease-in-out;
        }

        &__svg-container {
          font-size: 24px;
          color: var(--sub-merchants-item-svg-store-color);
        }

        &__name-container {
          flex: 1 1 auto;
          max-width: var(--sub-merchants-item-name-container-with);
          margin-inline-start: 6px;

          .ellipsis {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }

        &:hover {
          --sub-merchants-item-svg-store-color: var(--tamara-color-brand-700);

          color: var(--tamara-color-brand-700);
          background-color: var(--tamara-color-background-neutral-mid);
          transition: color 0.3s ease-in-out;
        }

        &:last-child {
          border-bottom: none;
        }
      }
    }
  }
}
</style>
